import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

const Admin = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user'
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

  useEffect(() => {
    // Redirect non-admin users
    if (!hasRole('admin')) {
      navigate('/hub');
      return;
    }
    fetchUsers();
  }, [hasRole, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users');
      setLoading(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setEditingUser(userToEdit.id);
    setFormData({
      firstName: userToEdit.first_name,
      lastName: userToEdit.last_name,
      email: userToEdit.email,
      password: '',
      role: userToEdit.role
    });
  };

  const handleCancel = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'user'
    });
  };

  const handleSave = async (userId) => {
    try {
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role
      };

      // Only include password if it was changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(
        `${API_BASE}/users/${userId}`,
        updateData,
        { withCredentials: true }
      );

      alert('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === user?.id) {
      alert('You cannot delete your own account');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(`${API_BASE}/users/${userId}`, {
          withCredentials: true
        });
        alert('User deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!hasRole('admin')) {
    return null;
  }

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>User Management</h1>
        <p>Manage user accounts, roles, and permissions</p>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.id === user?.id ? 'current-user' : ''}>
                {editingUser === u.id ? (
                  <>
                    <td>
                      <div className="edit-fields">
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="First Name"
                          className="edit-input"
                        />
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Last Name"
                          className="edit-input"
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email"
                        className="edit-input"
                      />
                    </td>
                    <td>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="edit-select"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleSave(u.id)}
                          className="btn btn-save"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="btn btn-cancel"
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="password-section">
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="New Password (leave blank to keep current)"
                          className="edit-input password-input"
                        />
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      <div className="user-name">
                        {u.first_name} {u.last_name}
                        {u.id === user?.id && <span className="you-badge">You</span>}
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`role-badge role-${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(u)}
                          className="btn btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="btn btn-delete"
                          disabled={u.id === user?.id}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
